import { HALLIDAY_DOMAIN, HALLIDAY_PAYMENTS_API_URL } from "@shared/constants";
import { TransactionReceipt, Asset, RampName, Address } from "@shared/types";
import { isMobile } from "@shared/utils/browserUtils";

import {
    AppMode,
    TextMode,
    PaymentCategoryGrouping,
    CustomStyles,
    Message,
    MessageResponse,
    MessageType,
    PaymentsWidgetSDKParams,
    Role,
    SDKOverrides,
    SendTransaction,
    SignMessage,
    SignTypedData,
    StatusCallback,
    PaymentCategoryOrder,
    FontName,
} from "./types";
import { _openPaymentsWidget, _openProviderWidget, _waitForPaymentStatus, logError } from "./utils";

const _windowManager = (() => {
    let widgetWindow: Window | null = null;
    let providerWindow: Window | null = null;
    let handlers: ((event: MessageEvent<Message>) => void)[] = [];

    const cleanMessageHandlers = () => {
        handlers.forEach((handler) => {
            window.removeEventListener("message", handler);
        });
        handlers = [];
    };

    const registerMessageHandlers = (newHandlers: ((event: MessageEvent<Message>) => void)[]) => {
        newHandlers.forEach((handler) => {
            window.addEventListener("message", handler);
        });
        handlers = newHandlers;
    };

    const openWidget = async (params: PaymentsWidgetSDKParams, ...args: any[]) => {
        const overrides = args[999] || {};

        let validatedParams: PaymentsWidgetSDKParams;
        try {
            validatedParams = PaymentsWidgetSDKParams.parse(params);
        } catch (error) {
            logError(error);
            throw error;
        }
        let validatedOverrides: SDKOverrides;
        try {
            validatedOverrides = SDKOverrides.parse(overrides);
        } catch (error) {
            logError(error);
            throw error;
        }

        const {
            dangerouslyOverrideHallidayDomainName,
            dangerouslyOverrideApiBaseUrl,
            dangerouslyOverrideIPAddress,
            dangerouslyOverrideFeatureFlags,
        } = validatedOverrides;

        // Extract the required parameters
        const { owner, statusCallback, funder, windowType } = validatedParams;
        const windowOrigin = dangerouslyOverrideHallidayDomainName || HALLIDAY_DOMAIN;
        const signMessage: SignMessage | undefined = owner?.signMessage;
        const signTypedData: SignTypedData | undefined = owner?.signTypedData;
        const sendTransaction: SendTransaction | undefined = funder?.sendTransaction || owner?.sendTransaction;
        const ownerAddress: Address | undefined = owner?.address;
        const funderAddress: Address | undefined = funder?.address;

        const hasOwner = Address.safeParse(ownerAddress).success && typeof signTypedData === "function";
        const hasTxHandler = Address.safeParse(funderAddress).success && typeof sendTransaction === "function";
        let hostOrigin: string | null = null;
        let appMode: AppMode = "FULL";
        try {
            hostOrigin = window.origin;
            if (windowType === "MODAL") {
                if (isMobile()) {
                    appMode = "OVERLAY";
                } else {
                    appMode = "MODAL";
                }
            }
        } catch (error) {
            // It is ok if access to window.origin is denied
        }

        // Clean up the previous commerce window
        cleanMessageHandlers();
        const messageHandlers: ((event: MessageEvent<Message>) => void)[] = [
            // handler for window close event
            (event: MessageEvent<Message>) => {
                // Verify message origin
                if (event.origin !== windowOrigin || event.source !== widgetWindow) {
                    return;
                }

                const { type } = event.data || {};
                if (type === MessageType.EVENT_WINDOW_CLOSE) {
                    cleanMessageHandlers();
                }
            },
        ];

        // Register message handler if owner is providered
        if (hasOwner) {
            const signatureMessageHandler = async (event: MessageEvent<Message>) => {
                // Verify message origin
                if (event.origin !== windowOrigin || event.source !== widgetWindow) {
                    return;
                }

                const { type, payload } = event.data || {};
                if (type === MessageType.ACTION_SIGN_MESSAGE) {
                    const { messageId, message, ownerAddress } = payload;
                    try {
                        let signature: string;
                        if (typeof signMessage === "function") {
                            signature = await signMessage({ message, ownerAddress });
                        } else {
                            throw new Error("No signMessage function provided");
                        }

                        widgetWindow?.postMessage(
                            { type, payload: { messageId, signature } } as MessageResponse,
                            windowOrigin
                        );
                    } catch (error) {
                        console.error("Error signing message", error);
                        widgetWindow?.postMessage(
                            {
                                type,
                                payload: {
                                    messageId,
                                    error: error instanceof Error ? error.message : error,
                                },
                            } as MessageResponse,
                            windowOrigin
                        );
                    }
                } else if (type === MessageType.ACTION_SIGN_TYPED_DATA) {
                    const { messageId, typedData, ownerAddress } = payload;
                    try {
                        let signature: string;
                        if (typeof signTypedData === "function") {
                            signature = await signTypedData({ typedData, ownerAddress });
                        } else {
                            throw new Error("No signTypedData function provided");
                        }

                        widgetWindow?.postMessage(
                            { type, payload: { messageId, signature } } as MessageResponse,
                            windowOrigin
                        );
                    } catch (error) {
                        console.error("Error signing typed data", error);
                        widgetWindow?.postMessage(
                            {
                                type,
                                payload: {
                                    messageId,
                                    error: error instanceof Error ? error.message : error,
                                },
                            } as MessageResponse,
                            windowOrigin
                        );
                    }
                }
            };

            messageHandlers.push(signatureMessageHandler);
        }

        if (hasTxHandler) {
            const transactionMessageHandler = async (event: MessageEvent<Message>) => {
                // Verify message origin
                if (event.origin !== windowOrigin || event.source !== widgetWindow) {
                    return;
                }

                const { type, payload } = event.data || {};
                if (type === MessageType.ACTION_TRANSACTION) {
                    const { messageId, chainConfig, transaction } = payload;
                    try {
                        let txReceipt: TransactionReceipt;
                        if (typeof sendTransaction === "function") {
                            txReceipt = await sendTransaction(transaction, chainConfig);
                        } else {
                            throw new Error("No sendTransaction function provided");
                        }
                        // Respond with the transaction receipt
                        widgetWindow?.postMessage(
                            { type, payload: { messageId, txReceipt } } as MessageResponse,
                            windowOrigin
                        );
                    } catch (error) {
                        console.error("Error handling transaction", error);
                        widgetWindow?.postMessage(
                            {
                                type,
                                payload: {
                                    messageId,
                                    error: error instanceof Error ? error.message : error,
                                },
                            } as MessageResponse,
                            windowOrigin
                        );
                    }
                }
            };

            messageHandlers.push(transactionMessageHandler);
        }

        if (statusCallback) {
            const statusMessageHandler = (event: MessageEvent<Message>) => {
                // Verify message origin
                if (event.origin !== windowOrigin || event.source !== widgetWindow) {
                    return;
                }

                const { type, payload } = event.data || {};
                if (type === MessageType.EVENT_ORDER_STATUS) {
                    statusCallback({ type, payload });
                }
            };

            messageHandlers.push(statusMessageHandler);
        }

        const providerWidgetActionHandler = async (event: MessageEvent<Message>) => {
            // Verify message origin
            if (event.origin !== windowOrigin || event.source !== widgetWindow) {
                return;
            }

            const { type, payload } = event.data || {};
            if (type === MessageType.ACTION_PROVIDER_WIDGET && widgetWindow) {
                const { url, redirectUrl, providerWidgetMode, workflowId } = payload || {};

                // Validate the payload
                if (!url || !redirectUrl || !providerWidgetMode || !workflowId) {
                    throw new Error("Invalid provider widget payload");
                }

                let tempWindow: Window | null = null;
                // Open the provider widget
                if (providerWidgetMode === "REDIRECT") {
                    widgetWindow!.location = url;
                    tempWindow = widgetWindow;
                } else if (providerWidgetMode === "POPUP") {
                    providerWindow = _openProviderWidget(url);
                    tempWindow = providerWindow;
                } else {
                    throw new Error("Invalid provider widget mode");
                }

                // Fetch the status of the payment
                await _waitForPaymentStatus({
                    apiKey: validatedParams.apiKey,
                    baseURL: dangerouslyOverrideApiBaseUrl || HALLIDAY_PAYMENTS_API_URL,
                    workflowId,
                    window: tempWindow!,
                });

                if (tempWindow?.closed) {
                    // The window was closed
                    return;
                }

                if (providerWidgetMode === "REDIRECT") {
                    widgetWindow!.location = redirectUrl;
                } else if (providerWidgetMode === "POPUP") {
                    providerWindow?.close();
                    providerWindow = null;
                }
            }
        };
        messageHandlers.push(providerWidgetActionHandler);

        // Open the user checkout modal in a popup window
        widgetWindow = _openPaymentsWidget({
            ...validatedParams,
            sandbox: validatedParams.sandbox || false,
            hasOwner,
            hasTxHandler,
            windowOrigin,
            apiBaseUrl: dangerouslyOverrideApiBaseUrl,
            hostOrigin,
            ownerAddress,
            funderAddress,
            ipAddress: dangerouslyOverrideIPAddress,
            featureFlags: dangerouslyOverrideFeatureFlags,
            appMode,
            fontName: validatedParams.fontName,
            paymentCategoryOrder: validatedParams.paymentCategoryOrder,
        });

        // Register message handlers
        registerMessageHandlers(messageHandlers);
    };

    return { openWidget };
})();

/**
 * Opens the Halliday Payments widget.
 *
 * This function initializes and opens the payment widget window in a popup or embedded mode.
 *
 * @param {PaymentsWidgetSDKParams} params The configurations for the payments widget, which includes:
 *
 * @param {!string} params.apiKey {string} Your API key for authorization.
 * @param {boolean=} params.sandbox {boolean} (optional) Whether the widget is in sandbox mode.
 * @param {string=} params.destinationAddress {string} (optional) The address of the destination of the widget.
 *
 * @param {Asset[]=} params.inputs {{@link Asset}[]} (optional) The input assets for the widget.
 * @param {Asset[]=} params.outputs {{@link Asset}[]} (optional) The output assets for the widget.
 * @param {RampName[]=} params.onramps {{@link RampName}[]} (optional) The onramps for the widget.
 * @param {RampName[]=} params.offramps {{@link RampName}[]} (optional) The offramps for the widget.
 *
 * @param {CustomStyles=} params.customStyles {{@link CustomStyles}} (optional) A list of custom styles to show in the widget.
 * @param {string=} params.targetElementId {string} (required if windowType is "EMBED") The ID of the DOM element where the widget should be embedded.
 * @param {("POPUP" | "EMBED")=} params.windowType {"POPUP" | "EMBED"} The desired display mode of the widget.
 *
 * @param {Role=} params.owner {{@link Role}} (optional) The owner that would have full access to payments workflow.
 * @param {Role=} params.funder {{@link Role}} (optional) The funder that initiates the funding.
 * @param {PaymentCategoryOrder=} params.paymentCategoryOrder {{@link PaymentCategoryOrder}} (optional) On the quote page, these are three displayed paymentcategories (empty or three elements).
 * @param {FontName=} params.fontName {{@link FontName}} (optional) The font name to use in the widget (haffer or wudoo-mono). Defaults to haffer.
 *
 * @param {StatusCallback=} params.statusCallback {{@link StatusCallback}} (optional) Callback to receive status events.
 */
export async function openHallidayPayments(params: PaymentsWidgetSDKParams, ...args: any[]): Promise<void> {
    try {
        return _windowManager.openWidget(params, ...args);
    } catch (error) {
        logError(error);
        throw error;
    }
}
